import React, { useEffect, useState, useContext } from "react";
import { PatientContext } from "../../lib/main-context";
import { Form, Col, Row } from "react-bootstrap";
import { useTranslation } from "../../i18n/TranslationProvider";

// hooks
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers";
import { useHistory, useLocation } from "react-router-dom";
import { format } from "date-fns";

// components
import Sallie from "../UIComponents/Sallie";
import LabelledInput from "../UIComponents/LabelledInput";
import DescribeCondition from "./validations/describeCondition";
import SubmitButton from "../UIComponents/SubmitButton";
import OnboardingStepHeader from "./OnboardingStepHeader";

// api
import { createCondition } from "../../api/TBIRequests";
import customizeMessages from "../../lib/customizeMessages";

const RecentCondition1 = ({ user, setOnboardingPercent }) => {
  const { patient } = useContext(PatientContext);
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const history = useHistory();
  const location = useLocation();

  const { register, handleSubmit, errors, watch } = useForm({
    resolver: yupResolver(DescribeCondition),
  });

  useEffect(() => {
    location.state?.fromPatientControl
      ? setOnboardingPercent(30)
      : setOnboardingPercent(20);
    setLoading(false);
  }, []);

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      data.dateOfSymptoms = format(new Date(data.dateOfSymptoms), "MM-dd-yyyy");
      await createCondition({ patientId: patient.patientId, data });
      setSubmitting(false);
      history.push("/RecentCondition2", {
        fromPatientControl: location.state?.fromPatientControl,
      });
    } catch (err) {
      history.push("/oops");
    }
  };

  const getPossibleMessages = () => ({
    mainHeader: {
      tbiPatient: t("recentcondition1.header.patient"),
      caregiver: t("recentcondition1.header.caregiver"),
    },
    sallieText: {
      tbiPatient: t("recentcondition1.sallie.patient"),
      caregiver: t("recentcondition1.sallie.caregiver"),
    },
    dateOfTbiLabel: {
      tbiPatient: t("recentcondition1.date.patient"),
      caregiver: t("recentcondition1.date.caregiver"),
    },
    addTbis: {
      tbiPatient: "You will have the option to add other TBI's after you set up your account.",
      caregiver: "You will have the option to add other TBI's after you set up your account.",
    },
    describeEventLabel: {
      tbiPatient: t("recentcondition1.describe.label.patient"),
      caregiver: t("recentcondition1.describe.label.caregiver"),
    },
    describeEventExtraInfo: {
      tbiPatient: t("recentcondition1.describe.info"),
      caregiver: t("recentcondition1.describe.info"),
    },
    describeEventPlaceholder: {
      tbiPatient: t("recentcondition1.describe.placeholder.patient"),
      caregiver: t("recentcondition1.describe.placeholder.caregiver"),
    },
    totalTbiLabel: {
      tbiPatient: t("recentcondition1.priortbi.label.patient"),
      caregiver: t("recentcondition1.priortbi.label.caregiver"),
    },
  });

  const getMessage = customizeMessages({ user, getPossibleMessages });

  return (
    <>
      {loading ? (
        t("common.loading")
      ) : (
        <>
          <div className="d-block">
            <OnboardingStepHeader text={getMessage("mainHeader")} />
            <Sallie text={getMessage("sallieText")} />
          </div>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <div className="d-block">
              <Form.Row>
                <Col>
                  <LabelledInput
                    autoFocus
                    required
                    labelAs="h6"
                    name="dateOfSymptoms"
                    placeholder="MM/DD/YYYY"
                    type="date"
                    date-format="mm/dd/yyyy"
                    inputRef={register}
                    label={getMessage("dateOfTbiLabel")}
                    errors={errors}
                  />
                </Col>
                <Col className="d-none align-items-end">
                  <div className="mb-3 ml-3">{getMessage("addTbis")}</div>
                </Col>
              </Form.Row>
              <LabelledInput
                inputAs="textarea"
                required
                labelAs="h6"
                extraInfo={getMessage("describeEventExtraInfo")}
                name="describeSymptoms"
                placeholder={getMessage("describeEventPlaceholder")}
                type="text"
                inputRef={register}
                label={getMessage("describeEventLabel")}
                errors={errors}
                rows={3}
              />
              <Form.Group>
                <Form.Label className={"h6-label"}>
                  {getMessage("totalTbiLabel")}
                  <span className="required">*</span>
                </Form.Label>
                <div>
                  <Form.Check
                    inline
                    label={t("recentcondition1.priortbi.yes")}
                    value="Yes"
                    name="hasTbiBefore"
                    type="radio"
                    id="tbiYes"
                    ref={register}
                  />
                  <Form.Check
                    inline
                    label={t("recentcondition1.priortbi.no")}
                    value="No, I do not recall"
                    name="hasTbiBefore"
                    type="radio"
                    id="tbiNo"
                    ref={register}
                    className="ml-3"
                  />
                </div>
                {errors.hasTbiBefore && (
                  <Form.Control.Feedback style={{ display: "block" }} type="invalid">
                    {errors.hasTbiBefore.message}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
              <Row>
                <SubmitButton type="submit" disabled={submitting}>
                  {t("onboarding.saveContinue")} &gt;
                </SubmitButton>
              </Row>
            </div>
          </Form>
        </>
      )}
    </>
  );
};

export default RecentCondition1;